import { FC, ReactElement, ReactNode, useEffect } from "react";
import useUserAuthStage from "src/state/hooks/useUserAuthStage";
import { useBloc } from "src/state/state";
import LoginOverlay from "src/ui/components/LoginOverlay/LoginOverlay";
import Loader from "../Loader/Loader";
import SubscriptionCubit from "src/state/SubscriptionCubit/SubscriptionCubit";
import { useNavigate } from "react-router-dom";
import UserCubit from "src/state/UserCubit/UserCubit";
import ReAuthenticateUsingTranscarent from "components/LoginForm/ReAuthenticateUsingTranscarent";
import ReAuthenticateUsingPersonify from "components/LoginForm/ReAuthenticateUsingPersonify";
import { useBlocNext } from "@blac/react";
import { View, WithAuthCubit } from "components/WithAuth/WithAuthCubit";
import AppAutoTriggers from "../AppAutoTriggers/AppAutoTriggers";
import LockedScreen from "components/LockedScreen/LockedScreen";
import { BiometricVerificationBloc } from "src/hybrid/components/BiometricVerification";
import { KnownPartnerSources } from "src/constants/partner";
import { getPartnerSourceByPayerId } from "src/constants/partnerConfig";

export interface Props {
  redirectIfNoToken?: boolean;
  loadProfile?: boolean;
  createAnonymousUser?: boolean;
  children?: ReactNode | undefined;
  allowPartialAuth?: boolean;
  /** If true, the user is redirected to a signup funnel if they don't have an active subscription */
  requiresSubscription?: boolean;
}

const WithAuth: FC<Props> = (props): ReactElement => {
  const navigate = useNavigate();
  const { authStage, isInitialCheckComplete, hasStoredCredentials } =
    useUserAuthStage();
  const [{ allSubscriptions }] = useBloc(SubscriptionCubit);

  const [{ userData }, { partnerId }] = useBloc(UserCubit);

  const [
    { isBiometricVerificationEnabled, isSessionVerified, requestedEmailLogin }
  ] = useBloc(BiometricVerificationBloc);

  const [{ view }, { initializeDependencies }] = useBlocNext(WithAuthCubit, {
    props
  });

  // Sync all auth-related state to WithAuthCubit which determines the current view
  useEffect(() => {
    initializeDependencies({
      props,
      navigate,
      allSubscriptions,
      payerId: partnerId,
      sessionAuthenticationMethod: userData?.sessionAuthenticationMethod,
      authStage,
      isInitialCheckComplete,
      hasStoredCredentials,
      isBiometricEnabled: isBiometricVerificationEnabled,
      isBiometricVerified: isSessionVerified,
      requestedEmailLogin
    });
  }, [
    props,
    navigate,
    allSubscriptions,
    partnerId,
    userData,
    authStage,
    isInitialCheckComplete,
    hasStoredCredentials,
    isBiometricVerificationEnabled,
    isSessionVerified,
    requestedEmailLogin,
    initializeDependencies
  ]);

  if (view === View.App) {
    return (
      <>
        {props.children}
        <AppAutoTriggers />
      </>
    );
  }

  if (view === View.Locked) {
    return <LockedScreen />;
  }

  if (view === View.Loading) {
    return <Loader gradient active fixed bg="branded" />;
  }

  if (view === View.Reauth) {
    const partnerSource = getPartnerSourceByPayerId(partnerId);
    if (partnerSource === KnownPartnerSources.personifyHealth) {
      return <ReAuthenticateUsingPersonify />;
    }
    return <ReAuthenticateUsingTranscarent />;
  }

  return <LoginOverlay />;
};

export default WithAuth;
